// frontend/src/components/Sidebar.tsx
import { useEffect, useMemo, ComponentType } from 'react';
import { resolveRosterAccess } from '../utils/rosterRoles';
import {
  extractRoleNames,
  isPlatformAdmin,
  isMechanicalClientTenant,
  canJobsAdmin,
  canJobFinancials,
  canFinancePl,
  canCostTransactionsReport,
  canTrainingAnalytics,
  canLearning,
  canLearningAdmin,
  canHREmployees,
  canHRAdmin,
  canHRInsights,
  canHRPerformance,
  canHRSitesPositions,
  isNotificationsAdmin as isNotificationsAdminGate,
  canOpenRoleCatalog as canOpenRoleCatalogGate,
  canManageHomepageContent,
  canViewVendorList,
  canAdministerVendors,
  canProcurementJobs,
  canViewMinimumManning,
} from '../utils/permissions';
import { approvalsDeskUrl } from '../utils/navigation';
import { useApprovalsDeskAccess } from '../hooks/useLandingProfile';
import { useDeskApprovalCount } from './home/approvalQueues';
import {
  HomeIcon,
  BriefcaseIcon,
  CalculatorIcon,
  PlusCircleIcon,
  QueueListIcon,
  ClipboardDocumentCheckIcon,
  ArchiveBoxXMarkIcon,
  MegaphoneIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  TagIcon,
  UserGroupIcon,
  UserPlusIcon,
  AcademicCapIcon,
  Squares2X2Icon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  BeakerIcon,
  QrCodeIcon,
  MapIcon,
  ClockIcon,
  BuildingOfficeIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  RectangleStackIcon,
  CloudIcon,
  UsersIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  BellIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { useSidebar } from '../contexts/SidebarContext';
import Button from './shared/Button';

/* ========================= Type Definitions ========================= */
interface Role {
  name?: string;
  [key: string]: unknown;
}

interface DbUser {
  roles?: Role[];
  [key: string]: unknown;
}

interface User {
  /** The session JWT — keys the shared landing-profile request. */
  token?: string;
  isActive?: boolean;
  _computedRoles?: string[];
  roles?: (string | Role)[];
  dbUser?: DbUser;
  role?: string;
  fullName?: string;
  name?: string;
  displayName?: string;
  email?: string;
  accountName?: string;
  account?: string;
  company?: string;
  orgName?: string;
  organization?: string;
  personCode?: string;
}

interface RoleGates {
  canJobs: boolean;
  canJobsAdmin: boolean;
  canJobFinancials: boolean;
  canFinancePl: boolean;
  canReports: boolean;
  // Cost Transactions report — its own role, because it exposes supplier,
  // AP invoice and cost/retail margin data that hr:admin has no business seeing.
  canCostTransactionsReport: boolean;
  canEAReport: boolean;
  canProcurement: boolean;
  canPurchaseOrders: boolean;
  canProcurementJobs: boolean;
  canVendorPrequal: boolean;
  canProcurementSiteAdmin: boolean;
  canFinancial: boolean;
  canHR: boolean;
  canProjects: boolean;
  canProjectControls: boolean;
  canCRM: boolean;
  canMechanicalPSV: boolean;
  canMechanicalPSVStart: boolean;
  canMechanicalPSVAdmin: boolean;
  canMechanicalValves: boolean;
  canMechanicalValvesStart: boolean;
  canMechanicalValvesAdmin: boolean;
  canMechanicalMovements: boolean;
  canFlange: boolean;
  flangeClientOnly: boolean;
  // Electrical EEHA
  canElectrical: boolean;
  canElectricalEEHA: boolean;
  canElectricalEEHAEdit: boolean;
  canElectricalEEHAConfigure: boolean;
  canElectricalEEHAPlatformAdmin: boolean;
  canFinancialExpenses: boolean;
  canFinancialInvoices: boolean;
  canFinancialRates: boolean;
  canFinancialExpensesApprover: boolean;
  canFinancialExpensesAdmin: boolean;
  canFinancialAdmin: boolean;
  canTraining: boolean;
  canTrainingDashboard: boolean;
  canTrainingCompetencyAdmin: boolean;
  canTrainingAnalytics: boolean;
  canLearning: boolean;
  canLearningAdmin: boolean;
  canApprovedVendors: boolean;
  canRosters: boolean;
  canRostersManage: boolean;
  canRostersViewer: boolean;
  canMinimumManning: boolean;
  canRostersShifts: boolean;
  /** Homepage Content admin — either content role (`comms:author`, `tips:author`). */
  canHomepageContent: boolean;
  canTimesheets: boolean;
  canTimesheetImport: boolean;
  canSantosFG: boolean;
  canTimesheetApprove: boolean;
  canTimesheetAdmin: boolean;
  canLeave: boolean;
  canLeaveInsights: boolean;
  canLeaveApprove: boolean;
  canLeaveAdmin: boolean;
  canPayslips: boolean;
  canHROrgChart: boolean;
  canHREmployees: boolean;
  canHRAdmin: boolean;
  canHRInsights: boolean;
  canHRPerformance: boolean;
  canHRSitesPositions: boolean;
  // Clients section
  canClients: boolean;
  canClientsInpex: boolean;
  isAdmin: boolean;
  // Scoped System Administration access (platform admin OR a delegated module
  // admin: mechanical:admin / mechanical_psv:admin / training_competency:admin)
  canUserAdmin: boolean;
  canRoleCatalog: boolean;
  canNotifications: boolean;
  // Viewer role - can see everything but cannot make changes
  isViewer: boolean;
  /** An external client account (only mechanical_clients:* roles). */
  mechanicalClientTenant: boolean;
}

interface SidebarProps {
  user: User;
  activeItem: string;
  setActiveItem: (_item: string) => void;
  activeCategory: string | null;
  setActiveCategory: (_category: string | null) => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (_open: boolean) => void;
}

interface SidebarLinkProps {
  label: string;
  value: string;
  activeItem: string;
  setActiveItem: (_item: string) => void;
  setIsSidebarOpen: (_open: boolean) => void;
  Icon?: ComponentType<{ className?: string }>;
  /** Optional custom click handler - if provided, overrides default navigation */
  onClick?: () => void;
  /** Optional custom href - if provided, overrides default `/?view=value` */
  href?: string;
}


/* ========================= role helpers =========================
 * Role extraction and every gate shared with MainContent.tsx now live in
 * utils/permissions.ts. The sidebar decides whether a menu entry appears and
 * MainContent decides whether the view behind it renders, so a rule the two
 * derive separately eventually disagrees -- which is exactly what happened to
 * canLearning. Gates below that are still local are ones only the sidebar has.
 */
function useRoleGates(user: User | undefined): RoleGates {
  const roles = useMemo(() => extractRoleNames(user), [user]);
  const has = (slug: string): boolean => roles.includes(String(slug).toLowerCase());
  const hasPrefix = (prefix: string): boolean =>
    roles.some(r => r.startsWith(String(prefix).toLowerCase()));
  const isAdmin = isPlatformAdmin(roles);

  // Roster access, resolved from the shared classifier (see utils/rosterRoles).
  const rosterAccess = useMemo(() => resolveRosterAccess(roles), [roles]);

  // Platform Viewer - can see ALL modules but cannot make any changes
  // This role is for executives who need visibility without edit capability
  const isViewer = has('platform:viewer');

  // A mechanical client tenant: their only module access is mechanical_clients:*
  // (base/legacy platform roles ignored). These users get the client-scoped
  // mechanical views only — no internal modules like Procurement or Flange.
  const mechanicalClientTenant = isMechanicalClientTenant(roles);

  return {
    canJobs: isAdmin || isViewer || has('jobs:default'),
    canJobsAdmin: canJobsAdmin(roles), // NOT viewer - admin only
    // Job Financials dashboard — gated to job_financial:admin during rollout;
    // platform admins keep access too. Widen later when the feature stabilises.
    canJobFinancials: canJobFinancials(roles),
    // Finance P&L — the management P&L from GL period balances. Three roles of
    // its own (config/financePlRoles.ts on the backend); the backend also
    // answers 404 for everyone while FINANCE_PL_ENABLED is not 'true'.
    canFinancePl: canFinancePl(roles),
    // Reports — HR headcount/employee reporting. hr:admin only (+ platform admin).
    canReports: isAdmin || has('hr:admin'),
    canCostTransactionsReport: canCostTransactionsReport(roles),
    // EA Requirements report exposes every employee's ordinary hourly
    // rate — pay-sensitive, so it carries its OWN role on top of
    // hr:admin. Only holders of hr_reports:ea (+ platform admins) see
    // the menu entry; the backend enforces the same rule.
    canEAReport: isAdmin || has('hr_reports:ea'),
    canProcurement:
      isAdmin || isViewer || has('procurement:default') || hasPrefix('procurement_request:'),
    // TEMPORARY (prod test): Purchase Orders submenu restricted to
    // procurement:admin + platform:admin while the PO/receipting flow is
    // validated on prod. To re-open to all procurement users, change the
    // gate on the submenu back to `canProcurement` (see ~line 1141).
    canPurchaseOrders: isAdmin || has('procurement:admin'),
    // Procurement → Jobs — temporary doorway onto the Project Controls tracker.
    canProcurementJobs: canProcurementJobs(roles),
    // Vendor Prequal admin — procurement:admin only (viewers cannot see submissions per data sensitivity)
    canVendorPrequal: canAdministerVendors(roles),
    // Procurement Site Config — admin-only knob; lives under Procurement.
    canProcurementSiteAdmin: isAdmin || has('procurement:admin') || has('procurement_request:*'),
    canHR: isAdmin || isViewer || has('hr:default'),
    canProjects: isAdmin || isViewer || has('projects:default'),
    // Project Controls top-level group — visible if any of its children are
    // visible (WBS Templates / Proposals via projects:default; Rates via
    // financial_rates:*). Recomputed inline against the same flags below so
    // we don't depend on declaration order.
    canProjectControls:
      isAdmin ||
      isViewer ||
      has('projects:default') ||
      hasPrefix('financial_rates:'),
    // CRM gate - show if user has any CRM role
    canCRM:
      isAdmin ||
      isViewer ||
      has('crm_admin') ||
      has('crm_clients:view') ||
      has('crm_clients:edit') ||
      has('crm_opportunities:view_east') ||
      has('crm_opportunities:view_west'),
    // Mechanical PSV gate. Clients (mechanical_clients:*) can read PSV
    // reports for their own valves — backend filters via the hierarchical
    // scope so they only see their own data. UI hides the "Start PSV
    // Test" link for them via canMechanicalPSVStart below.
    canMechanicalPSV:
      isAdmin || isViewer || has('mechanical_psv:default') || hasPrefix('mechanical_clients:'),
    // Only GRPS-side roles can start a new test — clients are read-only.
    canMechanicalPSVStart:
      isAdmin ||
      has('mechanical_psv:admin') ||
      has('mechanical_psv:default') ||
      has('mechanical_technician:psv') ||
      has('mechanical_technician:all') ||
      has('mechanical_workshop:all') ||
      has('technician'),
    canMechanicalPSVAdmin: isAdmin || has('mechanical_psv:admin'), // NOT viewer - admin only

    // Valves module (Control + General valves). Sister to PSV.
    // Clients (mechanical_clients:*) can read their own valve reports;
    // hidden "Start" link below restricts who can author.
    // mechanical:admin (module-wide mechanical admin) has full valves access.
    canMechanicalValves:
      isAdmin ||
      isViewer ||
      has('mechanical_valves:default') ||
      has('mechanical_valves:admin') ||
      has('mechanical:admin') ||
      hasPrefix('mechanical_clients:'),
    canMechanicalValvesStart:
      isAdmin ||
      has('mechanical_valves:admin') ||
      has('mechanical_valves:default') ||
      has('mechanical:admin') ||
      has('mechanical_technician:control') ||
      has('mechanical_technician:general') ||
      has('mechanical_technician:all') ||
      has('mechanical_workshop:all'),
    canMechanicalValvesAdmin: isAdmin || has('mechanical_valves:admin') || has('mechanical:admin'),
    // Movements gate (workshop / client / technician roles also unlock Mechanical section)
    canMechanicalMovements:
      isAdmin ||
      isViewer ||
      has('mechanical_psv:default') ||
      hasPrefix('mechanical_workshop:') ||
      hasPrefix('mechanical_clients:') ||
      hasPrefix('mechanical_technician:'),
    // Dedicated Flange gate: the mechanical_flange:* role family plus
    // admin/viewer/mechanical:admin. mechanical_clients:* alone does NOT
    // show Flange — PSV client tenants (e.g. Tamboran) must not see it.
    // If a flange client portal is needed later, grant mechanical_flange:readonly.
    canFlange:
      isAdmin ||
      isViewer ||
      has('mechanical:admin') ||
      hasPrefix('mechanical_flange:'),
    // A client tenant whose ONLY flange access is via mechanical_clients:* —
    // gets the read-only tracking portal, not the internal menu items.
    flangeClientOnly:
      !isAdmin &&
      !isViewer &&
      !has('mechanical:admin') &&
      !hasPrefix('mechanical_flange:') &&
      hasPrefix('mechanical_clients:'),

    // Electrical EEHA gates - matches backend role names
    // Any EEHA role gives view access
    canElectrical: isAdmin || hasPrefix('electrical_eeha:'),
    canElectricalEEHA: isAdmin || hasPrefix('electrical_eeha:'),
    // Edit access: :edit or :admin suffix, or platform admin
    canElectricalEEHAEdit:
      isAdmin ||
      has('electrical_eeha:admin') ||
      roles.some(r => r.endsWith(':edit') && r.startsWith('electrical_eeha:')) ||
      roles.some(r => r.endsWith(':admin') && r.startsWith('electrical_eeha:')),
    // Configure access: :admin suffix on any client, or platform admin
    canElectricalEEHAConfigure:
      isAdmin ||
      has('electrical_eeha:admin') ||
      roles.some(r => r.endsWith(':admin') && r.startsWith('electrical_eeha:')),
    // Platform admin: electrical_eeha:admin or platform:admin/admin
    canElectricalEEHAPlatformAdmin: isAdmin || has('electrical_eeha:admin'),

    // Financial gates (new structure)
    canFinancialExpenses: isAdmin || isViewer || hasPrefix('financial_expenses:'),
    canFinancialInvoices: isAdmin || isViewer || hasPrefix('financial_invoices:'),
    canFinancialRates: isAdmin || isViewer || hasPrefix('financial_rates:') || has('rates:view'),
    canFinancialAdmin: isAdmin || has('financial:admin'), // NOT viewer - admin only

    // Financial root - show if any financial role
    canFinancial:
      isAdmin ||
      isViewer ||
      hasPrefix('financial_expenses:') ||
      hasPrefix('financial_invoices:') ||
      hasPrefix('financial_rates:') ||
      hasPrefix('finance_pl:') ||
      has('financial:admin'),

    // Financial → Expenses sub-permissions (viewers can see but not approve/admin)
    // Approvers are determined by job assignments (JA/JM/SM), so show menu to anyone with expenses access
    // The backend filters actual approvals based on job assignments
    canFinancialExpensesApprover: isAdmin || hasPrefix('financial_expenses:'), // Show to anyone with any expenses role
    canFinancialExpensesAdmin: isAdmin || has('financial_expenses:admin'), // NOT viewer

    // Training gate - any training_competency:* role can access the training section
    canTraining: isAdmin || isViewer || hasPrefix('training_competency:'),
    // Training Dashboard — any training_competency:* role (including self) can view the dashboard
    canTrainingDashboard: isAdmin || isViewer || hasPrefix('training_competency:'),
    canTrainingCompetencyAdmin: isAdmin || has('training_competency:admin'), // NOT viewer
    // Analytics pages (Gap Analysis, Compliance) — admin-only; employee-scoped roles
    // like training_competency:self or training_competency:<siteId> must not see these.
    canTrainingAnalytics: canTrainingAnalytics(roles),
    // Learning (LMS) — platform admins and training admins only, while the
    // module is still in development. No `lms:*` role grants visibility yet and
    // isViewer is excluded, so `lms:default` can exist and be assignable to
    // future trainees without opening anything today.
    //
    // The backend is the actual control: middleware/lmsAuth.ts gates on
    // LMS_VISIBILITY, and hiding a menu entry protects nothing because most
    // views here are reachable by typing ?view=X. This list must therefore stay
    // in step with the exception in lmsAuth — a role that sees the menu but
    // 403s on every API call is worse than no menu entry at all.
    //
    // When the module opens to learners, widen this to
    // `isAdmin || hasPrefix('lms:')` and flip LMS_VISIBILITY=roles together.
    canLearning: canLearning(roles),
    canLearningAdmin: canLearningAdmin(roles),
    // Approved Vendor List (utils/permissions).
    canApprovedVendors: !mechanicalClientTenant && canViewVendorList(roles),

    // Rosters gate. Classification lives in utils/rosterRoles so the sidebar,
    // the Rosters page and the planner board cannot disagree about what a role
    // name means — a menu entry that leads to a 403 is worse than no entry.
    //
    // Site-scoped editors (`rosters:<siteCode>`) and region roles were excluded
    // here while the per-site roles had no Role documents and so could not be
    // assigned. They can be now, and this is what makes them reachable: an
    // editor gets the same "Manage Rosters" entry an admin does, scoped by the
    // backend to the sites they hold.
    // Minimum manning reaches some who hold no roster role (mmr:admin, mmr:viewer:<site>).
    canRosters: isAdmin || rosterAccess.canAccess || canViewMinimumManning(roles),
    // The management screen — opens the planner in edit mode. Admins and any
    // site or region editor.
    canRostersManage: isAdmin || rosterAccess.canEdit,
    // The read-only screen. Offered only to users who hold no edit grant at all;
    // an editor who also views a third site reaches it through the same board.
    canRostersViewer: !isAdmin && rosterAccess.isViewerOnly,
    canMinimumManning: canViewMinimumManning(roles),
    // My Shifts is for roster users; minimum manning alone does not open it.
    canRostersShifts: isAdmin || rosterAccess.canAccess,

    canHomepageContent: canManageHomepageContent(roles),

    // Timesheets gate
    canTimesheets:
      isAdmin || has('timesheets:admin') || has('timesheets:default') || has('timesheets:export'),
    canTimesheetImport: isAdmin || has('timesheets:import') || has('timesheets:admin'),
    // Santos / Fieldglass admin — own role for this client-specific flow.
    canSantosFG: isAdmin || has('timesheet_santos:admin'),
    canTimesheetApprove: isAdmin || has('timesheets:admin'),
    canTimesheetAdmin: isAdmin || has('timesheets:admin') || has('timesheets:export'), // NOT viewer

    // Leave gate
    canLeave: isAdmin || has('leave:default') || has('leave:admin'),
    // Leave & Attendance Insights names individuals and their sick leave, so it
    // has its OWN role rather than riding on hr:admin (operational HR) or
    // platform:viewer (handed out for read-only dashboards). The real gate is
    // the router; this only decides whether to draw the menu entry, and the two
    // MUST stay in step — a menu entry that 403s is worse than no menu entry,
    // and a hidden entry protects nothing since ?view=X is reachable by hand.
    canLeaveInsights: isAdmin || has('hr:insights:leave'),
    canLeaveApprove: isAdmin || has('timesheets:admin') || has('leave:admin'),
    canLeaveAdmin: isAdmin || has('timesheets:admin') || has('leave:admin'),

    // Payslips gate
    canPayslips: isAdmin || has('payslips:default'),

    // HR Org Chart gate - requires hr_orgchart:viewer role
    canHROrgChart: isAdmin || isViewer || hasPrefix('hr_orgchart:'),
    canHREmployees: canHREmployees(roles),
    canHRAdmin: canHRAdmin(roles),
    canHRInsights: canHRInsights(roles),
    canHRPerformance: canHRPerformance(roles),
    canHRSitesPositions: canHRSitesPositions(roles),

    // Clients section gate - clients:admin for all clients, clients_inpex:* for Inpex only
    canClients:
      isAdmin ||
      isViewer ||
      has('platform:admin') ||
      has('clients:admin') ||
      hasPrefix('clients_inpex:'),
    canClientsInpex:
      isAdmin ||
      isViewer ||
      has('platform:admin') ||
      has('clients:admin') ||
      hasPrefix('clients_inpex:'),

    // Scoped System Administration: platform admins OR a module's delegated
    // admin. Mechanical and training admins each get a restricted view (invite/
    // list users + manage only their own module's roles) — enforced in
    // UserManagement + backend.
    canUserAdmin:
      isAdmin ||
      has('mechanical:admin') ||
      has('mechanical_psv:admin') ||
      has('training_competency:admin') ||
      // Operations: Scheduled Jobs only. Read through the shared helper rather
      // than another inline `has(...)` — CLAUDE.md is explicit that a role
      // gate expressed twice eventually disagrees with itself, and this entry
      // gates two links, only one of which the operations role can actually
      // open (see canRoleCatalog below).
      isNotificationsAdminGate(roles),

    // Settings → Notifications (bell sections and email audiences).
    canNotifications: isNotificationsAdminGate(roles),

    // Role Catalog: platform admins and the delegated admins of backend
    // services/roleDelegation.ts (mechanical, PSV, training, HR). It shares
    // `canUserAdmin`'s menu section but NOT its audience: offering it to the
    // operations role produced a link that navigated to a blank screen.
    canRoleCatalog: canOpenRoleCatalogGate(roles),

    isAdmin,
    isViewer,
    mechanicalClientTenant,
  };
}

/* ============================== Styling helpers ============================== */
/** Primary nav item classes (top-level categories & standalone links) */
const navCls = (active: boolean): string =>
  `group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 relative focus:outline-none ${
    active ? 'bg-white/[0.12] text-white' : 'text-white/80 hover:bg-white/[0.07] hover:text-white'
  }`;

/** Icon classes for nav items */
const navIconCls = (active: boolean): string =>
  `h-5 w-5 mr-3 flex-shrink-0 transition-colors duration-150 ${
    active ? 'text-blue-300' : 'text-blue-300/70 group-hover:text-blue-200'
  }`;

/** Sub-nav item classes (nested category toggles) */
const subNavCls = (active: boolean): string =>
  `group flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150 relative focus:outline-none ${
    active ? 'bg-white/[0.10] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
  }`;

/** Icon classes for sub-nav items */
const subIconCls = (active: boolean): string =>
  `h-5 w-5 mr-3 flex-shrink-0 transition-colors duration-150 ${
    active ? 'text-blue-300' : 'text-blue-300/60 group-hover:text-blue-200'
  }`;

/** Dev/Test badge */
const devBadge =
  'ml-auto px-2 py-0.5 text-2xs font-bold uppercase tracking-wider rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30';
const testBadge =
  'ml-auto px-2 py-0.5 text-2xs font-bold uppercase tracking-wider rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/30';

/** Count after an entry's label (the Approval Desk's waiting approvals) */
const countBadge =
  'ml-auto px-2 py-0.5 text-2xs font-bold tabular-nums rounded-full bg-blue-400/15 text-blue-200 border border-blue-400/30';

/**
 * How many approvals wait on the desk. Rendered only inside the desk's gated
 * entry, so nobody who cannot open the desk reads the queues. Nothing while
 * no count is known (or after a failed read), and nothing at 0. While the desk
 * is open it publishes the count itself, so no reads of our own.
 */
function DeskApprovalCount({ token, deskOpen }: { token?: string; deskOpen: boolean }) {
  const count = useDeskApprovalCount(token, !deskOpen);
  if (!count) return null;
  return (
    <span className={countBadge} aria-label={`${count} waiting`}>
      {count}
    </span>
  );
}

/** Chevron for expandable items */
const chevronCls = (open: boolean): string =>
  `h-4 w-4 ml-auto flex-shrink-0 text-white/30 transition-transform duration-200 ${open ? 'rotate-90' : ''}`;

/* =================================== Sidebar =================================== */
export default function Sidebar({
  user,
  activeItem,
  setActiveItem,
  activeCategory,
  setActiveCategory,
  isSidebarOpen,
  setIsSidebarOpen,
}: SidebarProps) {
  // Desktop collapse comes from context rather than another prop: App already
  // owns it, and threading it through would mean touching every caller.
  const { isSidebarCollapsed } = useSidebar();
  const {
    canJobs,
    canJobsAdmin,
    canJobFinancials,
    canFinancePl,
    canProjects,
    canProjectControls,
    canReports,
    canCostTransactionsReport,
    canEAReport,
    canProcurement,
    canPurchaseOrders,
    canProcurementJobs,
    canVendorPrequal,
    canProcurementSiteAdmin,
    canFinancial,
    canHR,
    canCRM,
    canMechanicalPSV,
    canMechanicalPSVStart,
    canMechanicalPSVAdmin,
    canMechanicalValves,
    canMechanicalValvesStart,
    canMechanicalValvesAdmin,
    canMechanicalMovements,
    canFlange,
    flangeClientOnly,
    canElectrical,
    canElectricalEEHA,
    canElectricalEEHAConfigure,
    canElectricalEEHAPlatformAdmin: _canElectricalEEHAPlatformAdmin,
    canFinancialExpenses,
    canFinancialInvoices,
    canFinancialRates,
    canFinancialExpensesApprover,
    canFinancialExpensesAdmin,
    canFinancialAdmin,
    canTraining,
    canTrainingDashboard,
    canTrainingCompetencyAdmin,
    canTrainingAnalytics,
    canLearning,
    canLearningAdmin,
    canApprovedVendors,
    canRosters,
    canRostersManage,
    canRostersViewer,
    canMinimumManning,
    canRostersShifts,
    canHomepageContent,
    canTimesheets,
    canTimesheetImport: _canTimesheetImport,
    canSantosFG,
    canTimesheetApprove: _canTimesheetApprove,
    canTimesheetAdmin: _canTimesheetAdmin,
    canLeave,
    canLeaveInsights,
    canLeaveApprove: _canLeaveApprove,
    canLeaveAdmin: _canLeaveAdmin,
    canPayslips,
    canHROrgChart,
    canHREmployees,
    canHRAdmin,
    canHRInsights,
    canHRPerformance,
    canHRSitesPositions,
    canClients,
    canClientsInpex,
    canUserAdmin,
    canRoleCatalog,
    canNotifications,
    isAdmin,
    isViewer: _isViewer,
    mechanicalClientTenant,
  } = useRoleGates(user);

  // Approval Desk entry: the approvals:desk role, or one the server granted
  // this session (hooks/useLandingProfile.ts).
  const deskRoles = useMemo(() => extractRoleNames(user), [user]);
  const { allowed: canOpenDesk } = useApprovalsDeskAccess(user?.token, deskRoles);

  const hasPersonCode = !!(user?.personCode || user?.dbUser?.personCode);

  // When the active view is set externally (URL deep-link, browser back/forward,
  // navigation from another page), expand the matching sidebar category so the
  // user can see where they are. Only fires when no category is open yet so we
  // don't steal user-initiated expansions.
  useEffect(() => {
    if (activeCategory) return;
    const ITEM_TO_CATEGORY: Record<string, string> = {
      ApprovedVendorList: 'VendorsLive',
      ApprovedVendorDetail: 'VendorsLive',
      VendorPrequalList: 'VendorsLive',
      DormantVendors: 'VendorsLive',
      VendorPrequalDetail: 'VendorsLive',
      // Training → Competency subgroup. All these items live under the
      // Competency sub-section, so setting 'Competency' expands both the
      // Training parent and the Competency subgroup containing them.
      TrainingCompetencyDashboard: 'Competency',
      TrainingBookings: 'Competency',
      MatrixConfigAdmin: 'Competency',
      CertificateApprovalQueue: 'Competency',
      MyTrainingProfile: 'Competency',
      GapAnalysis: 'Competency',
      ComplianceReport: 'Competency',
      CompetencyAdmin: 'Competency',
      // Learning
      LmsCourseAdmin: 'Learning',
      LmsCourseDetail: 'Learning',
      LmsCoursePlayer: 'Learning',
      LmsCourseBuilder: 'Learning',
      LmsAdmin: 'Learning',
      LmsLearnerAdmin: 'Learning',
      LmsEnrolmentAdmin: 'Learning',
      LmsMyLearning: 'Learning',
      LeaveAttendanceInsights: 'HR',
    };
    const target = ITEM_TO_CATEGORY[activeItem];
    if (target) setActiveCategory(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  if (!user?.isActive) return null;

  const toggleCategory = (category: string, parent: string | null = null): void => {
    if (activeCategory === category) {
      setActiveCategory(parent && activeCategory !== parent ? parent : null);
    } else {
      setActiveCategory(category);
    }
    setIsSidebarOpen(true);
  };

  const closeOnMobile = (): void => {
    // Close sidebar on mobile/tablet/iPad (under xl breakpoint 1280px)
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setIsSidebarOpen(false);
    }
  };

  // generic link helper
  const pickItem = (value: string): void => {
    setActiveItem(value);
    closeOnMobile();
  };

  return (
    <>
      {/* Overlay for mobile/tablet when sidebar is open. It covers the area
          below the top bar only, so the bar's menu button still closes it. */}
      <div
        className={`
          absolute inset-0 z-40 xl:hidden
          bg-black/60 backdrop-blur-sm
          transition-opacity duration-300
          ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setIsSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Main sidebar. Positioned in App's row below the top bar, which owns
          the logo, the title and the top safe-area inset. */}
      <aside
        className={`flex flex-col z-50 transform transition-transform duration-300 ease-in-out
          w-[268px] absolute inset-y-0 left-0 pl-safe pb-safe-mobile
          bg-gradient-to-b from-chrome-700 via-chrome-800 to-chrome-900
          border-r border-blue-400/20
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isSidebarCollapsed ? 'xl:-translate-x-full' : 'xl:translate-x-0'}`}
      >
        {/* Subtle grid pattern overlay, continuous with the top bar's */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04] chrome-grid" />

        <nav className="relative z-10 flex-1 overflow-y-auto py-3 px-2.5 scrollbar-hide">
          <ul className="space-y-0.5">
            {/* ══════════════════════════════════════════════════════════════
                LIVE MODULES SECTION
               ══════════════════════════════════════════════════════════════ */}

            {/* Home — the landing page for every employee. Not for external
                client accounts: it is internal company content. */}
            {!mechanicalClientTenant && (
              <li>
                <button
                  onClick={() => {
                    // Clear category first, then set active item, then close sidebar
                    // This ensures Jobs category is collapsed before navigation
                    setActiveCategory(null);
                    setActiveItem('Home');
                    closeOnMobile();
                  }}
                  className={navCls(activeItem === 'Home' && !activeCategory)}
                >
                  <HomeIcon className={navIconCls(activeItem === 'Home' && !activeCategory)} />
                  <span>Home</span>
                </button>
              </li>
            )}

            {/* Approval Desk (view id 'ApprovalsDesk', which old email links
                carry) — approvals:desk only (canOpenDesk above). Everyone
                else's to-dos reach them through the bell. */}
            {canOpenDesk && (
            <li>
              <button
                onClick={() => {
                  setActiveCategory(null);
                  setActiveItem('ApprovalsDesk');
                  closeOnMobile();
                }}
                className={navCls(activeItem === 'ApprovalsDesk' && !activeCategory)}
              >
                <ClipboardDocumentCheckIcon
                  className={navIconCls(activeItem === 'ApprovalsDesk' && !activeCategory)}
                />
                <span>Approval Desk</span>
                <DeskApprovalCount token={user?.token} deskOpen={activeItem === 'ApprovalsDesk'} />
              </button>
            </li>
            )}

            {/* Time & Pay - visible if user has access to Timesheets, Leave, or Payslips */}
            {(canTimesheets || canLeave || canPayslips) && (
              <li>
                <button
                  onClick={() => toggleCategory('TimesheetsLeave')}
                  className={navCls(
                    ['TimesheetsLeave', 'TSTimesheets', 'TSLeave'].includes(activeCategory || '')
                  )}
                >
                  <ClockIcon
                    className={navIconCls(
                      ['TimesheetsLeave', 'TSTimesheets', 'TSLeave'].includes(activeCategory || '')
                    )}
                  />
                  <span>Time & Pay</span>
                  <ChevronRightIcon
                    className={chevronCls(
                      ['TimesheetsLeave', 'TSTimesheets', 'TSLeave'].includes(activeCategory || '')
                    )}
                  />
                </button>

                {['TimesheetsLeave', 'TSTimesheets', 'TSLeave'].includes(activeCategory || '') && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {/* Timesheets */}
                    {canTimesheets && (
                      <li>
                        <SidebarLink
                          label="Timesheets"
                          value="TimesheetPage"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ClockIcon}
                        />
                      </li>
                    )}

                    {canSantosFG && (
                      <li>
                        <SidebarLink
                          label="Santos Fieldglass"
                          value="SantosFGAdmin"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ClockIcon}
                        />
                      </li>
                    )}

                    {/* Leave */}
                    {canLeave && (
                      <li>
                        <SidebarLink
                          label="Leave"
                          value="Leave"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={CalendarDaysIcon}
                        />
                      </li>
                    )}

                    {/* Payslips */}
                    {canPayslips && hasPersonCode ? (
                      <li>
                        <SidebarLink
                          label="Payslips"
                          value="HRPayslips"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={CreditCardIcon}
                        />
                      </li>
                    ) : canPayslips ? (
                      <li>
                        <Button variant="onDark" size="sm" disabled title="Your account is not yet linked to a HR profile" fullWidth>
                          <CreditCardIcon className="h-5 w-5 mr-3 flex-shrink-0 text-blue-300/30" />
                          <span>Payslips (unavailable)</span>
                        </Button>
                      </li>
                    ) : null}
                  </ul>
                )}
              </li>
            )}

            {/* Rosters (LIVE), beside Time & Pay. */}
            {canRosters && (
              <li>
                <button
                  onClick={() => toggleCategory('Rosters')}
                  className={navCls(activeCategory === 'Rosters')}
                >
                  <CalendarDaysIcon className={navIconCls(activeCategory === 'Rosters')} />
                  <span>Rosters</span>
                  <ChevronRightIcon className={chevronCls(activeCategory === 'Rosters')} />
                </button>
                {activeCategory === 'Rosters' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canRostersShifts && (
                      <li>
                        <SidebarLink
                          label="My Shifts"
                          value="RostersDashboard"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={Squares2X2Icon}
                        />
                      </li>
                    )}
                    {canRostersManage && (
                      <li>
                        <SidebarLink
                          label="Manage Rosters"
                          value="RostersAdmin"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={Cog6ToothIcon}
                        />
                      </li>
                    )}
                    {canRostersViewer && (
                      <li>
                        <SidebarLink
                          label="View Rosters"
                          value="RostersViewer"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={CalendarDaysIcon}
                        />
                      </li>
                    )}
                    {/* Editors reach it as a tab in Manage Rosters; this is for those with no roster screen. */}
                    {canMinimumManning && !canRostersManage && !canRostersViewer && (
                      <li>
                        <SidebarLink
                          label="Minimum Manning"
                          value="RostersManning"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ShieldCheckIcon}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Jobs (LIVE - Jobs submenu only, Estimates moved to Dev) */}
            {canJobs && (
              <li>
                <button
                  onClick={() => toggleCategory('Jobs')}
                  className={navCls(activeCategory === 'Jobs')}
                >
                  <BriefcaseIcon className={navIconCls(activeCategory === 'Jobs')} />
                  <span>Jobs</span>
                  <ChevronRightIcon className={chevronCls(activeCategory === 'Jobs')} />
                </button>

                {activeCategory === 'Jobs' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    <li>
                      <SidebarLink
                        label="View Jobs"
                        value="JobList"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={QueueListIcon}
                      />
                    </li>
                    {canJobFinancials && (
                      <li>
                        <SidebarLink
                          label="Job Financials"
                          value="FinancialReportsHome"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ChartBarIcon}
                        />
                      </li>
                    )}
                    {canJobFinancials && (
                      <li>
                        <SidebarLink
                          label="Chart of Accounts"
                          value="ChartOfAccountsBrowser"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ChartBarIcon}
                        />
                      </li>
                    )}
                    {canJobsAdmin && (
                      <li>
                        <SidebarLink
                          label="Jobs Admin"
                          value="JobsAdmin"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={Cog6ToothIcon}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* CRM — moved out of Development / Testing to sit below Jobs (Kurt, 2026-10-20) */}
            {canCRM && (
              <li>
                <button
                  onClick={() => toggleCategory('CRM')}
                  className={navCls(activeCategory === 'CRM')}
                >
                  <UserGroupIcon className={navIconCls(activeCategory === 'CRM')} />
                  <span>CRM</span>
                  <ChevronRightIcon className={chevronCls(activeCategory === 'CRM')} />
                </button>
                {activeCategory === 'CRM' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    <li>
                      <SidebarLink
                        label="CRM Dashboard"
                        value="CRMDashboard"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={Squares2X2Icon}
                      />
                    </li>
                    <li>
                      <SidebarLink
                        label="Clients"
                        value="PipelineClients"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={UserGroupIcon}
                      />
                    </li>
                    {/* Opportunities deliberately has no menu entry (Kurt,
                        2026-08-25): the flow is client-centric — track them
                        on the client's Opportunities tab, or click through
                        from the CRM Dashboard's "Manage opportunities". */}
                  </ul>
                )}
              </li>
            )}

            {/* Project Controls */}
            {canProjectControls && (
              <li>
                <button
                  onClick={() => toggleCategory('ProjectControls')}
                  className={navCls(activeCategory === 'ProjectControls')}
                >
                  <RectangleStackIcon className={navIconCls(activeCategory === 'ProjectControls')} />
                  <span>Project Controls</span>
                  <ChevronRightIcon className={chevronCls(activeCategory === 'ProjectControls')} />
                </button>

                {activeCategory === 'ProjectControls' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canProjects && (
                      <li>
                        <SidebarLink
                          label="WBS Templates"
                          value="WBSTemplates"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={QueueListIcon}
                        />
                      </li>
                    )}
                    {canProjects && (
                      <li>
                        <SidebarLink
                          label="Proposals"
                          value="Proposals"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ClipboardDocumentListIcon}
                        />
                      </li>
                    )}
                    {canProjects && (
                      <li>
                        <SidebarLink
                          label="Jobs"
                          value="ProjectJobs"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={BriefcaseIcon}
                        />
                      </li>
                    )}
                    {canFinancialRates && (
                      <li>
                        <SidebarLink
                          label="Rates"
                          value="Rates"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={TagIcon}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Financial */}
            {canFinancial && (
              <li>
                <button
                  onClick={() => toggleCategory('Financial')}
                  className={navCls(
                    ['Financial', 'Expenses', 'Invoices', 'FinancialAdmin'].includes(
                      activeCategory || ''
                    )
                  )}
                >
                  <BanknotesIcon
                    className={navIconCls(
                      ['Financial', 'Expenses', 'Invoices', 'FinancialAdmin'].includes(
                        activeCategory || ''
                      )
                    )}
                  />
                  <span>Financial</span>
                  <ChevronRightIcon
                    className={chevronCls(
                      ['Financial', 'Expenses', 'Invoices', 'FinancialAdmin'].includes(
                        activeCategory || ''
                      )
                    )}
                  />
                </button>

                {['Financial', 'Expenses', 'Invoices', 'FinancialAdmin'].includes(
                  activeCategory || ''
                ) && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {/* Expenses */}
                    {canFinancialExpenses && (
                      <li>
                        <button
                          onClick={() => toggleCategory('Expenses', 'Financial')}
                          className={subNavCls(activeCategory === 'Expenses')}
                        >
                          <CreditCardIcon className={subIconCls(activeCategory === 'Expenses')} />
                          <span>Expenses</span>
                          <ChevronRightIcon className={chevronCls(activeCategory === 'Expenses')} />
                        </button>
                        {activeCategory === 'Expenses' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Expenses Dashboard"
                                value="ExpensesDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                            {/* Without approvals:desk, the standalone Expense
                                Approvals screen, as before the desk existed. */}
                            {canFinancialExpensesApprover && !canOpenDesk && (
                              <li>
                                <SidebarLink
                                  label="Expense Approvals"
                                  value="ExpenseApprovals"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ClipboardDocumentCheckIcon}
                                />
                              </li>
                            )}
                            {canFinancialExpensesApprover && canOpenDesk && (
                              <li>
                                <SidebarLink
                                  label="Expense Approvals"
                                  value="ApprovalsDesk"
                                  activeItem={activeItem}
                                  setActiveItem={setActiveItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ClipboardDocumentCheckIcon}
                                  href={approvalsDeskUrl('expenses')}
                                  onClick={() => {
                                    setActiveItem('ApprovalsDesk');
                                    window.history.replaceState({}, '', approvalsDeskUrl('expenses'));
                                    window.dispatchEvent(new Event('navigate-home'));
                                    closeOnMobile();
                                  }}
                                />
                              </li>
                            )}
                            {canFinancialExpensesAdmin && (
                              <li>
                                <SidebarLink
                                  label="Expense Admin"
                                  value="ExpenseAdmin"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={WrenchScrewdriverIcon}
                                />
                              </li>
                            )}
                            {canFinancialExpensesAdmin && (
                              <li>
                                <SidebarLink
                                  label="Receipt Auto-Match"
                                  value="ReceiptAutoMatchDashboard"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={WrenchScrewdriverIcon}
                                />
                              </li>
                            )}
                            {canFinancialExpensesAdmin && (
                              <li>
                                <SidebarLink
                                  label="Shared Card AI Log"
                                  value="SharedCardAiLog"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={WrenchScrewdriverIcon}
                                />
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    )}

                    {/* Invoices */}
                    {canFinancialInvoices && (
                      <li>
                        <button
                          onClick={() => toggleCategory('Invoices', 'Financial')}
                          className={subNavCls(activeCategory === 'Invoices')}
                        >
                          <CurrencyDollarIcon
                            className={subIconCls(activeCategory === 'Invoices')}
                          />
                          <span>Invoices</span>
                          <ChevronRightIcon className={chevronCls(activeCategory === 'Invoices')} />
                        </button>
                        {activeCategory === 'Invoices' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Create Client Invoice"
                                value="ClientInvoiceForm"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={PlusCircleIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="View Client Invoices"
                                value="ClientInvoiceList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}


                    {/* Admin MOA */}
                    {canFinancialAdmin && (
                      <li>
                        <button
                          onClick={() => toggleCategory('FinancialAdmin', 'Financial')}
                          className={subNavCls(activeCategory === 'FinancialAdmin')}
                        >
                          <ShieldCheckIcon
                            className={subIconCls(activeCategory === 'FinancialAdmin')}
                          />
                          <span>Admin</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'FinancialAdmin')}
                          />
                        </button>
                        {activeCategory === 'FinancialAdmin' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Manual of Authorities"
                                value="ManualOfAuthority"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ClipboardDocumentCheckIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}

                    {/* Finance P&L — the management P&L from GL period balances */}
                    {canFinancePl && (
                      <li>
                        <SidebarLink
                          label="Finance P&L"
                          value="FinancePnlReport"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={CalculatorIcon}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Reports — HR reporting (hr:admin) plus the separately-gated
                Cost Transactions report, so either role opens the section. */}
            {(canReports || canCostTransactionsReport) && (
              <li>
                <button
                  onClick={() => toggleCategory('Reports')}
                  className={navCls(activeCategory === 'Reports')}
                >
                  <ChartBarIcon className={navIconCls(activeCategory === 'Reports')} />
                  <span>Reports</span>
                  <ChevronRightIcon className={chevronCls(activeCategory === 'Reports')} />
                </button>

                {activeCategory === 'Reports' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canReports && (
                    <li className="px-2 pt-1 pb-0.5 text-2xs font-semibold uppercase tracking-wider text-blue-200/60">
                      HR
                    </li>
                    )}
                    {canReports && (
                      <>
                        <li>
                          <SidebarLink
                            label="Headcount"
                            value="HeadcountReport"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={UsersIcon}
                          />
                        </li>
                        {canEAReport && (
                          <li>
                            <SidebarLink
                              label="EA Requirements"
                              value="EARequirementsReport"
                              activeItem={activeItem}
                              setActiveItem={pickItem}
                              setIsSidebarOpen={setIsSidebarOpen}
                              Icon={UsersIcon}
                            />
                          </li>
                        )}
                        <li>
                          <SidebarLink
                            label="Medicals"
                            value="MedicalsReport"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={UsersIcon}
                          />
                        </li>
                      </>
                    )}
                    {canCostTransactionsReport && (
                      <>
                        <li className="px-2 pt-2 pb-0.5 text-2xs font-semibold uppercase tracking-wider text-blue-200/60">
                          Financial
                        </li>
                        <li>
                          <SidebarLink
                            label="Cost Transactions"
                            value="CostTransactionsReport"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={ChartBarIcon}
                          />
                        </li>
                      </>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* HR (LIVE - Employees, Org Chart, Admin) */}
            {(canHREmployees || canHROrgChart || canHRAdmin || canHRPerformance) && (
              <li>
                <button
                  onClick={() => toggleCategory('HR')}
                  className={navCls(activeCategory === 'HR')}
                >
                  <UserGroupIcon className={navIconCls(activeCategory === 'HR')} />
                  <span>HR</span>
                  <ChevronRightIcon className={chevronCls(activeCategory === 'HR')} />
                </button>

                {activeCategory === 'HR' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canHREmployees && canHRInsights && (
                      <li>
                        <SidebarLink
                          label="HR Insights"
                          value="HRInsights"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ChartBarSquareIcon}
                        />
                      </li>
                    )}
                    {canLeaveInsights && (
                      <li>
                        <SidebarLink
                          label="Leave Insights"
                          value="LeaveAttendanceInsights"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ChartBarIcon}
                        />
                      </li>
                    )}
                    {canHREmployees && (
                      <li>
                        <SidebarLink
                          label="Employees"
                          value="HREmployees"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={UsersIcon}
                        />
                      </li>
                    )}
                    {canHRPerformance && (
                      <li>
                        <SidebarLink
                          label="Performance"
                          value="HRPerformance"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={ClipboardDocumentCheckIcon}
                        />
                      </li>
                    )}
                    {canHROrgChart && (
                      <li>
                        <SidebarLink
                          label="Org Charts"
                          value="OrgChart"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={Squares2X2Icon}
                        />
                      </li>
                    )}
                    {canHRSitesPositions && (
                      <li>
                        <SidebarLink
                          label="Sites & Positions"
                          value="HRSitesPositions"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={BuildingOfficeIcon}
                        />
                      </li>
                    )}
                    {canHRAdmin && (
                      <li>
                        <SidebarLink
                          label="Admin"
                          value="HRAdmin"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={Cog6ToothIcon}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Training (LIVE) */}
            {canTraining && (
              <li>
                <button
                  onClick={() => toggleCategory('Training')}
                  className={navCls(['Training', 'Competency'].includes(activeCategory || ''))}
                >
                  <AcademicCapIcon
                    className={navIconCls(
                      ['Training', 'Competency'].includes(activeCategory || '')
                    )}
                  />
                  <span>Training</span>
                  <ChevronRightIcon
                    className={chevronCls(
                      ['Training', 'Competency'].includes(activeCategory || '')
                    )}
                  />
                </button>

                {['Training', 'Competency'].includes(activeCategory || '') && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canTrainingDashboard && (
                      <li>
                        <button
                          onClick={() => toggleCategory('Competency', 'Training')}
                          className={subNavCls(activeCategory === 'Competency')}
                        >
                          <CheckBadgeIcon className={subIconCls(activeCategory === 'Competency')} />
                          <span>Competency</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'Competency')}
                          />
                        </button>
                        {activeCategory === 'Competency' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Dashboard"
                                value="TrainingCompetencyDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Training Bookings"
                                value="TrainingBookings"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={CalendarDaysIcon}
                              />
                            </li>
                            {canTrainingCompetencyAdmin && (
                              <li>
                                <SidebarLink
                                  label="Matrices"
                                  value="MatrixConfigAdmin"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={RectangleStackIcon}
                                />
                              </li>
                            )}
                            {canTrainingCompetencyAdmin && (
                              <li>
                                <SidebarLink
                                  label="Certificates"
                                  value="CertificateApprovalQueue"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ClipboardDocumentCheckIcon}
                                />
                              </li>
                            )}
                            {canTrainingAnalytics && (
                              <li>
                                <SidebarLink
                                  label="Gap Analysis"
                                  value="GapAnalysis"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ExclamationTriangleIcon}
                                />
                              </li>
                            )}
                            {canTrainingAnalytics && (
                              <li>
                                <SidebarLink
                                  label="Compliance"
                                  value="ComplianceReport"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ShieldCheckIcon}
                                />
                              </li>
                            )}
                            {canTrainingCompetencyAdmin && (
                              <li>
                                <SidebarLink
                                  label="Admin"
                                  value="CompetencyAdmin"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={WrenchScrewdriverIcon}
                                />
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Learning (LMS) lives in the Development / Testing section below,
                not here: the module is not open to anyone but platform and
                training admins yet. */}

            {/* Procurement (LIVE — Vendors + Requisitions + Client Request) */}
            {(canApprovedVendors || canProcurement || canProcurementJobs) && (
              <li>
                <button
                  onClick={() => toggleCategory('ProcurementLive')}
                  className={navCls(
                    ['ProcurementLive', 'VendorsLive', 'Requisitions', 'Request'].includes(
                      activeCategory || ''
                    )
                  )}
                >
                  <BeakerIcon
                    className={navIconCls(
                      ['ProcurementLive', 'VendorsLive', 'Requisitions', 'Request'].includes(
                        activeCategory || ''
                      )
                    )}
                  />
                  <span>Procurement</span>
                  <ChevronRightIcon
                    className={chevronCls(
                      ['ProcurementLive', 'VendorsLive', 'Requisitions', 'Request'].includes(
                        activeCategory || ''
                      )
                    )}
                  />
                </button>

                {['ProcurementLive', 'VendorsLive', 'Requisitions', 'Request'].includes(
                  activeCategory || ''
                ) && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canApprovedVendors && (
                      <li>
                        <button
                          onClick={() => toggleCategory('VendorsLive', 'ProcurementLive')}
                          className={subNavCls(activeCategory === 'VendorsLive')}
                        >
                          <CheckBadgeIcon
                            className={subIconCls(activeCategory === 'VendorsLive')}
                          />
                          <span>Vendors</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'VendorsLive')}
                          />
                        </button>
                        {activeCategory === 'VendorsLive' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Approved Vendor List"
                                value="ApprovedVendorList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            {canVendorPrequal && (
                              <li>
                                <SidebarLink
                                  label="Vendor Prequal"
                                  value="VendorPrequalList"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ClipboardDocumentCheckIcon}
                                />
                              </li>
                            )}
                            {/* Vendor lifecycle. Same gate as Vendor Prequal —
                                the backend requires procurement:admin. */}
                            {canVendorPrequal && (
                              <li>
                                <SidebarLink
                                  label="Dormant Vendors"
                                  value="DormantVendors"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={ArchiveBoxXMarkIcon}
                                />
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    )}
                    {/* TEMPORARY (prod test): gated to admins via canPurchaseOrders.
                        Revert to `canProcurement` to re-open to all procurement users. */}
                    {canPurchaseOrders && (
                      <li>
                        <button
                          onClick={() => toggleCategory('Requisitions', 'ProcurementLive')}
                          className={subNavCls(activeCategory === 'Requisitions')}
                        >
                          <ClipboardDocumentCheckIcon
                            className={subIconCls(activeCategory === 'Requisitions')}
                          />
                          <span>PO&apos;s</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'Requisitions')}
                          />
                        </button>
                        {activeCategory === 'Requisitions' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="New Purchase Order"
                                value="RequisitionEntryForm"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={PlusCircleIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Purchase Order Approvals"
                                value="PurchaseOrderApprovals"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="View PO&apos;s"
                                value="RequisitionList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Receipt PO&apos;s"
                                value="PurchaseOrderReceipting"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}
                    {/* Procurement → Jobs: the Project Controls purchasing tracker, reached
                        from here by job managers until Project Controls is the home for it. */}
                    {canProcurementJobs && (
                      <li>
                        <SidebarLink
                          label="Jobs"
                          value="ProcurementJobs"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={QueueListIcon}
                        />
                      </li>
                    )}
                    {canProcurement && (
                      <li>
                        <button
                          onClick={() => toggleCategory('Request', 'ProcurementLive')}
                          className={subNavCls(activeCategory === 'Request')}
                        >
                          <ChatBubbleLeftRightIcon
                            className={subIconCls(activeCategory === 'Request')}
                          />
                          <span>Client Request</span>
                          <ChevronRightIcon className={chevronCls(activeCategory === 'Request')} />
                        </button>
                        {activeCategory === 'Request' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Create Request"
                                value="ClientRequest"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={PlusCircleIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="View Requests"
                                value="ClientRequestList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}
                    {canProcurementSiteAdmin && (
                      <li>
                        <SidebarLink
                          label="Sites Config"
                          value="ProcurementSiteConfig"
                          activeItem={activeItem}
                          setActiveItem={pickItem}
                          setIsSidebarOpen={setIsSidebarOpen}
                          Icon={CheckBadgeIcon}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Mechanical */}
            {(canMechanicalPSV || canMechanicalValves || canMechanicalMovements || canFlange) && (
              <li>
                <button
                  onClick={() => toggleCategory('Mechanical')}
                  className={navCls(
                    [
                      'Mechanical',
                      'MechanicalPSV',
                      'MechanicalValves',
                      'MechanicalMovements',
                      'MechanicalFlange',
                      'MechanicalAdmin',
                    ].includes(activeCategory || '')
                  )}
                >
                  <WrenchScrewdriverIcon
                    className={navIconCls(
                      [
                        'Mechanical',
                        'MechanicalPSV',
                        'MechanicalValves',
                        'MechanicalMovements',
                        'MechanicalFlange',
                        'MechanicalAdmin',
                      ].includes(activeCategory || '')
                    )}
                  />
                  <span>Mechanical</span>
                  <ChevronRightIcon
                    className={chevronCls(
                      [
                        'Mechanical',
                        'MechanicalPSV',
                        'MechanicalValves',
                        'MechanicalMovements',
                        'MechanicalFlange',
                        'MechanicalAdmin',
                      ].includes(activeCategory || '')
                    )}
                  />
                </button>

                {[
                  'Mechanical',
                  'MechanicalPSV',
                  'MechanicalValves',
                  'MechanicalMovements',
                  'MechanicalFlange',
                  'MechanicalAdmin',
                ].includes(activeCategory || '') && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {/* PSV Sub-menu */}
                    {canMechanicalPSV && (
                      <li>
                        <button
                          onClick={() => toggleCategory('MechanicalPSV', 'Mechanical')}
                          className={subNavCls(activeCategory === 'MechanicalPSV')}
                        >
                          <BeakerIcon className={subIconCls(activeCategory === 'MechanicalPSV')} />
                          <span>PSV</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'MechanicalPSV')}
                          />
                        </button>

                        {activeCategory === 'MechanicalPSV' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="PSV Dashboard"
                                value="PSVDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                            {canMechanicalPSVStart && (
                              <li>
                                <SidebarLink
                                  label="Start PSV Test"
                                  value="PSVReportForm"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={PlusCircleIcon}
                                />
                              </li>
                            )}
                            <li>
                              <SidebarLink
                                label="View PSV Tests"
                                value="PSVReportList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="PSV Insights"
                                value="PSVInsightsDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ChartBarIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}

                    {/* Valves Sub-menu — Control + General valve inspection
                        reports. Sister module to PSV. See
                        docs/development/VALVES_MODULE_PLAN.md */}
                    {canMechanicalValves && (
                      <li>
                        <button
                          onClick={() => toggleCategory('MechanicalValves', 'Mechanical')}
                          className={subNavCls(activeCategory === 'MechanicalValves')}
                        >
                          <BeakerIcon
                            className={subIconCls(activeCategory === 'MechanicalValves')}
                          />
                          <span>Valves</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'MechanicalValves')}
                          />
                        </button>

                        {activeCategory === 'MechanicalValves' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Valves Dashboard"
                                value="ValveDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                            {canMechanicalValvesStart && (
                              <li>
                                <SidebarLink
                                  label="Start Valve Test"
                                  value="ValveReportForm"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={PlusCircleIcon}
                                />
                              </li>
                            )}
                            <li>
                              <SidebarLink
                                label="View Valve Tests"
                                value="ValveReportList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Valves Insights"
                                value="ValveInsightsDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ChartBarIcon}
                              />
                            </li>
                            {canMechanicalValvesAdmin && (
                              <li>
                                <SidebarLink
                                  label="Valves Admin"
                                  value="ValvesConfigPage"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={Cog6ToothIcon}
                                />
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    )}

                    {/* Movements Sub-menu */}
                    {canMechanicalMovements && (
                      <li>
                        <button
                          onClick={() => toggleCategory('MechanicalMovements', 'Mechanical')}
                          className={subNavCls(activeCategory === 'MechanicalMovements')}
                        >
                          <ArrowPathIcon
                            className={subIconCls(activeCategory === 'MechanicalMovements')}
                          />
                          <span>Movements</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'MechanicalMovements')}
                          />
                        </button>

                        {activeCategory === 'MechanicalMovements' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="New Booking"
                                value="MechanicalNewBooking"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={PlusCircleIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="View List"
                                value="MechanicalMovementsList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Scan QR"
                                value="MechanicalScan"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QrCodeIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Insights"
                                value="MovementsInsightsDashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ChartBarIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}

                    {/* Flange Management Sub-menu */}
                    {canFlange && (
                      <li>
                        <button
                          onClick={() => toggleCategory('MechanicalFlange', 'Mechanical')}
                          className={subNavCls(activeCategory === 'MechanicalFlange')}
                        >
                          <QueueListIcon
                            className={subIconCls(activeCategory === 'MechanicalFlange')}
                          />
                          <span>Flange Management</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'MechanicalFlange')}
                          />
                        </button>

                        {activeCategory === 'MechanicalFlange' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            {!flangeClientOnly && (
                              <>
                                <li>
                                  <SidebarLink
                                    label="Dashboard"
                                    value="FlangeDashboard"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={ChartBarIcon}
                                  />
                                </li>
                                <li>
                                  <SidebarLink
                                    label="Joints"
                                    value="FlangeJointList"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={QueueListIcon}
                                  />
                                </li>
                                <li>
                                  <SidebarLink
                                    label="Site Map"
                                    value="FlangeSiteMapViewer"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={MapIcon}
                                  />
                                </li>
                                <li>
                                  <SidebarLink
                                    label="Scan QR"
                                    value="FlangeScanPage"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={QrCodeIcon}
                                  />
                                </li>
                                <li>
                                  <SidebarLink
                                    label="Campaigns"
                                    value="FlangeCampaigns"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={QueueListIcon}
                                  />
                                </li>
                                <li>
                                  <SidebarLink
                                    label="Work-packs"
                                    value="FlangeWorkPacks"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={QueueListIcon}
                                  />
                                </li>
                                <li>
                                  <SidebarLink
                                    label="Calculator"
                                    value="FlangeCalculator"
                                    activeItem={activeItem}
                                    setActiveItem={pickItem}
                                    setIsSidebarOpen={setIsSidebarOpen}
                                    Icon={CalculatorIcon}
                                  />
                                </li>
                              </>
                            )}
                            <li>
                              <SidebarLink
                                label="Client Tracking"
                                value="FlangePortal"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ChartBarIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Configuration"
                                value="FlangeConfig"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Cog6ToothIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}

                    {/* Admin Sub-menu */}
                    {canMechanicalPSVAdmin && (
                      <li>
                        <button
                          onClick={() => toggleCategory('MechanicalAdmin', 'Mechanical')}
                          className={subNavCls(activeCategory === 'MechanicalAdmin')}
                        >
                          <Cog6ToothIcon
                            className={subIconCls(activeCategory === 'MechanicalAdmin')}
                          />
                          <span>Admin</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'MechanicalAdmin')}
                          />
                        </button>

                        {activeCategory === 'MechanicalAdmin' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="PSV Configuration"
                                value="PSVConfigPage"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Cog6ToothIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Notification Config"
                                value="MechanicalNotificationConfig"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={BellIcon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Electrical (EEHA) */}
            {canElectrical && (
              <li>
                <button
                  onClick={() => toggleCategory('Electrical')}
                  className={navCls(['Electrical', 'EEHA'].includes(activeCategory || ''))}
                >
                  <BoltIcon
                    className={navIconCls(['Electrical', 'EEHA'].includes(activeCategory || ''))}
                  />
                  <span>Electrical</span>
                  <ChevronRightIcon
                    className={chevronCls(['Electrical', 'EEHA'].includes(activeCategory || ''))}
                  />
                </button>

                {['Electrical', 'EEHA'].includes(activeCategory || '') && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {/* EEHA Sub-menu */}
                    {canElectricalEEHA && (
                      <li>
                        <button
                          onClick={() => toggleCategory('EEHA', 'Electrical')}
                          className={subNavCls(activeCategory === 'EEHA')}
                        >
                          <ShieldCheckIcon className={subIconCls(activeCategory === 'EEHA')} />
                          <span>EEHA Manager</span>
                          <ChevronRightIcon className={chevronCls(activeCategory === 'EEHA')} />
                        </button>
                        {activeCategory === 'EEHA' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Dashboard"
                                value="EEHADashboard"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Tags"
                                value="EEHATagList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={TagIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Equipment"
                                value="EEHAEquipmentList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={QueueListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Inspections"
                                value="EEHAInspectionList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ClipboardDocumentCheckIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Defects"
                                value="EEHADefectList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ExclamationTriangleIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Lots"
                                value="EEHALotList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={RectangleStackIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Risk Assessments"
                                value="EEHARiskAssessmentList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ChartBarIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Samples"
                                value="EEHASampleList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={BeakerIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Workpacks"
                                value="EEHAWorkpackList"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={ClipboardDocumentListIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Offline Sync"
                                value="EEHAOfflineSyncManager"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={CloudIcon}
                              />
                            </li>
                            <li>
                              <SidebarLink
                                label="Site Maps"
                                value="EEHASiteMapViewer"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                            {/* Configuration - visible to client admins and platform admins */}
                            {canElectricalEEHAConfigure && (
                              <li>
                                <SidebarLink
                                  label="Configuration"
                                  value="EEHAConfigPage"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={WrenchScrewdriverIcon}
                                />
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Clients (LIVE) */}
            {canClients && (
              <li>
                <button
                  onClick={() => toggleCategory('ClientsSection')}
                  className={navCls(
                    ['ClientsSection', 'ClientsInpex'].includes(activeCategory || '')
                  )}
                >
                  <BuildingOfficeIcon
                    className={navIconCls(
                      ['ClientsSection', 'ClientsInpex'].includes(activeCategory || '')
                    )}
                  />
                  <span>Clients</span>
                  <ChevronRightIcon
                    className={chevronCls(
                      ['ClientsSection', 'ClientsInpex'].includes(activeCategory || '')
                    )}
                  />
                </button>

                {['ClientsSection', 'ClientsInpex'].includes(activeCategory || '') && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {/* Inpex Sub-menu */}
                    {canClientsInpex && (
                      <li>
                        <button
                          onClick={() => toggleCategory('ClientsInpex', 'ClientsSection')}
                          className={subNavCls(activeCategory === 'ClientsInpex')}
                        >
                          <BuildingOfficeIcon
                            className={subIconCls(activeCategory === 'ClientsInpex')}
                          />
                          <span>Inpex</span>
                          <ChevronRightIcon
                            className={chevronCls(activeCategory === 'ClientsInpex')}
                          />
                        </button>
                        {activeCategory === 'ClientsInpex' && (
                          <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                            <li>
                              <SidebarLink
                                label="Gate Entry Dashboard"
                                value="InpexGateEntry"
                                activeItem={activeItem}
                                setActiveItem={pickItem}
                                setIsSidebarOpen={setIsSidebarOpen}
                                Icon={Squares2X2Icon}
                              />
                            </li>
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* SETTINGS (visible to all users) */}
            <li>
              <button
                onClick={() => toggleCategory('Settings')}
                className={navCls(activeCategory === 'Settings')}
              >
                <Cog6ToothIcon className={navIconCls(activeCategory === 'Settings')} />
                <span>Settings</span>
                <ChevronRightIcon className={chevronCls(activeCategory === 'Settings')} />
              </button>

              {activeCategory === 'Settings' && (
                <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                  <li>
                    <SidebarLink
                      label="Delegation of Authority"
                      value="UserSettings"
                      activeItem={activeItem}
                      setActiveItem={pickItem}
                      setIsSidebarOpen={setIsSidebarOpen}
                      Icon={ClipboardDocumentCheckIcon}
                    />
                  </li>
                  {canUserAdmin && (
                    <li>
                      <SidebarLink
                        label="System Administration"
                        value="UserManagement"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={ShieldCheckIcon}
                      />
                    </li>
                  )}
                  {canHomepageContent && (
                    <li>
                      <SidebarLink
                        label="Content Admin Panel"
                        value="HomepageContentAdmin"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={MegaphoneIcon}
                      />
                    </li>
                  )}
                  {canNotifications && (
                    <li>
                      <SidebarLink
                        label="Notifications"
                        value="NotificationSettings"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={BellIcon}
                      />
                    </li>
                  )}
                  {canRoleCatalog && (
                    <li>
                      <SidebarLink
                        label="Role Catalog"
                        value="RoleCatalog"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={ShieldCheckIcon}
                      />
                    </li>
                  )}
                  {isAdmin && (
                    <li>
                      <SidebarLink
                        label="Public Holidays"
                        value="PublicHolidaysAdmin"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={CalendarDaysIcon}
                      />
                    </li>
                  )}
                  {isAdmin && (
                    <li>
                      <SidebarLink
                        label="API Errors"
                        value="ApiErrors"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={ExclamationTriangleIcon}
                      />
                    </li>
                  )}
                </ul>
              )}
            </li>

            {/* ══════════════════════════════════════════════════════════════
                DEVELOPMENT / TESTING SECTION
               ══════════════════════════════════════════════════════════════ */}
            <li className="mt-4 pt-4">
              <div className="flex items-center gap-2 px-3 mb-2">
                <span className="text-2xs font-bold text-blue-300/50 uppercase tracking-[0.12em] whitespace-nowrap">
                  Development / Testing
                </span>
                <div className="flex-1 h-px bg-blue-400/20" />
              </div>
            </li>

            {/* Learning (LMS) (DEV — platform admins and training admins only).
                Moved here from the live section: the module is still gated, and
                listing it alongside shipped features implies it is one. */}
            {canLearning && (
              <li>
                <button
                  onClick={() => toggleCategory('Learning')}
                  className={navCls(activeCategory === 'Learning')}
                >
                  <BookOpenIcon className={navIconCls(activeCategory === 'Learning')} />
                  <span>Learning</span>
                  <span className={devBadge}>Dev</span>
                </button>

                {activeCategory === 'Learning' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {/*
                      First on purpose. This is the entry every trainee uses;
                      everything below it administers somebody else and is for a
                      handful of people.
                    */}
                    <li>
                      <SidebarLink
                        label="My Learning"
                        value="LmsMyLearning"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={AcademicCapIcon}
                      />
                    </li>
                    <li>
                      <SidebarLink
                        label="Courses"
                        value="LmsCourseAdmin"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={RectangleStackIcon}
                      />
                    </li>
                    {canLearningAdmin && (
                      <>
                        <li>
                          <SidebarLink
                            label="Learners"
                            value="LmsLearnerAdmin"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={UsersIcon}
                          />
                        </li>
                        <li>
                          <SidebarLink
                            label="Enrolments"
                            value="LmsEnrolmentAdmin"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={ClipboardDocumentCheckIcon}
                          />
                        </li>
                        <li>
                          <SidebarLink
                            label="Learning Admin"
                            value="LmsAdmin"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={Cog6ToothIcon}
                          />
                        </li>
                      </>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Estimates (DEV - moved from Jobs) */}
            {canJobs && (
              <li>
                <button
                  onClick={() => toggleCategory('DevEstimates')}
                  className={navCls(activeCategory === 'DevEstimates')}
                >
                  <CalculatorIcon className={navIconCls(activeCategory === 'DevEstimates')} />
                  <span>Estimates</span>
                  <span className={testBadge}>Test</span>
                </button>
                {activeCategory === 'DevEstimates' && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    <li>
                      <SidebarLink
                        label="Create Estimates"
                        value="ProcurementEstimates"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={PlusCircleIcon}
                      />
                    </li>
                    <li>
                      <SidebarLink
                        label="View Estimates"
                        value="ProcurementEstimatesList"
                        activeItem={activeItem}
                        setActiveItem={pickItem}
                        setIsSidebarOpen={setIsSidebarOpen}
                        Icon={QueueListIcon}
                      />
                    </li>
                  </ul>
                )}
              </li>
            )}

            {/* HR (DEV - Onboarding, Training Board) */}
            {canHR && (
              <li>
                <button
                  onClick={() => toggleCategory('DevHR')}
                  className={navCls(['DevHR', 'DevHRTraining'].includes(activeCategory || ''))}
                >
                  <UserGroupIcon
                    className={navIconCls(
                      ['DevHR', 'DevHRTraining'].includes(activeCategory || '')
                    )}
                  />
                  <span>HR</span>
                  <span className={devBadge}>Dev</span>
                </button>
                {['DevHR', 'DevHRTraining'].includes(activeCategory || '') && (
                  <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/20">
                    {canHR && (
                      <>
                        <li>
                          <SidebarLink
                            label="Onboarding Form"
                            value="HrOnboardingForm"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={UserPlusIcon}
                          />
                        </li>
                        <li>
                          <SidebarLink
                            label="Onboarding List"
                            value="HrOnboardingList"
                            activeItem={activeItem}
                            setActiveItem={pickItem}
                            setIsSidebarOpen={setIsSidebarOpen}
                            Icon={QueueListIcon}
                          />
                        </li>
                        <li>
                          <button
                            onClick={() => toggleCategory('DevHRTraining', 'DevHR')}
                            className={subNavCls(activeCategory === 'DevHRTraining')}
                          >
                            <AcademicCapIcon
                              className={subIconCls(activeCategory === 'DevHRTraining')}
                            />
                            <span>Training</span>
                            <ChevronRightIcon
                              className={chevronCls(activeCategory === 'DevHRTraining')}
                            />
                          </button>
                          {activeCategory === 'DevHRTraining' && (
                            <ul className="mt-1 ml-3 pl-3 space-y-0.5 border-l border-blue-400/15">
                              <li>
                                <SidebarLink
                                  label="Training Board"
                                  value="HRWorkItems"
                                  activeItem={activeItem}
                                  setActiveItem={pickItem}
                                  setIsSidebarOpen={setIsSidebarOpen}
                                  Icon={Squares2X2Icon}
                                />
                              </li>
                            </ul>
                          )}
                        </li>
                      </>
                    )}
                  </ul>
                )}
              </li>
            )}

          </ul>
        </nav>

        {/* Footer */}
        <div className="relative z-10 flex-shrink-0 border-t border-white/[0.08]">
          {/* Privacy link */}
          <div className="px-4 py-2">
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-300/40 hover:text-blue-200/70 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </aside>

    </>
  );
}

/* ============================== SidebarLink helper ============================== */
function SidebarLink({
  label,
  value,
  activeItem,
  setActiveItem,
  setIsSidebarOpen,
  Icon,
  onClick: customOnClick,
  href: customHref,
}: SidebarLinkProps) {
  const IconComp = Icon ?? QueueListIcon;
  const isActive = activeItem === value;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    // Allow middle-click (button 1) and Ctrl/Cmd+click to open in new tab naturally
    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      return; // Let browser handle opening in new tab
    }

    // For regular left-clicks, prevent default and use SPA navigation
    e.preventDefault();

    // Use custom onClick if provided, otherwise default navigation
    if (customOnClick) {
      customOnClick();
    } else {
      setActiveItem(value);
    }

    // Close sidebar on mobile/tablet/iPad (under xl breakpoint 1280px)
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setIsSidebarOpen(false);
    }
  };

  // Build the href for the link (supports opening in new tab)
  const href = customHref || `/?view=${encodeURIComponent(value)}`;

  return (
    <a
      href={href}
      onClick={handleClick}
      onAuxClick={handleClick}
      className={`group flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150 relative ${
        isActive
          ? 'bg-white/[0.10] text-white'
          : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
      }`}
    >
      <IconComp
        className={`h-5 w-5 mr-3 flex-shrink-0 transition-colors duration-150 ${
          isActive ? 'text-blue-300' : 'text-blue-300/60 group-hover:text-blue-200'
        }`}
      />
      <span className={isActive ? 'font-semibold' : ''}>{label}</span>
    </a>
  );
}

// Export the role gates hook for use in other components
// Components can use this to check isViewer and hide action buttons
export { useRoleGates };
export type { RoleGates, User as SidebarUser };
